import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Save, Mail, Eye, Send } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import AdminLayout from "@/components/AdminLayout";

const TEMPLATE_LABELS = {
  platform_welcome_owner: "Willkommen → Salon-Inhaber",
  platform_password_reset: "Passwort zurücksetzen → Admin/Inhaber",
  platform_double_opt_in: "Double-Opt-In → neuer Salon-Inhaber",
  platform_trial_ending: "Testphase endet bald → Salon-Inhaber",
  platform_renewal_reminder: "Abo-Verlängerung Erinnerung → Salon-Inhaber",
  platform_payment_failed: "Zahlung fehlgeschlagen → Salon-Inhaber",
  platform_plan_change: "Paket umgestellt → Salon-Inhaber",
  platform_new_salon_alert: "Neuer Salon angemeldet → SuperAdmin",
};

const VARIABLES = {
  platform_welcome_owner: ["owner_name", "salon_name", "salon_url", "login_url", "plan_name", "plan_price", "trial_info"],
  platform_password_reset: ["user_name", "reset_url"],
  platform_double_opt_in: ["owner_name", "verify_url"],
  platform_trial_ending: ["owner_name", "salon_name", "trial_end_date", "days_left", "plan_name", "plan_price", "billing_url"],
  platform_renewal_reminder: ["owner_name", "salon_name", "renewal_date", "plan_name", "plan_price", "billing_url"],
  platform_payment_failed: ["owner_name", "salon_name", "billing_url"],
  platform_plan_change: ["owner_name", "salon_name", "old_plan_name", "new_plan_name", "new_plan_price", "billing_url"],
  platform_new_salon_alert: ["salon_name", "salon_slug", "owner_name", "owner_email", "plan_name", "industry_name", "created_at", "admin_url"],
};

export default function SuperEmailTemplates() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState({ subject: "", body: "" });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    const { data } = await api.get("/super/email-templates");
    setList(data);
    if (data[0] && !active) setActive(data[0]);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setTestTo(user?.email || ""); }, [user]);

  const draftKey = useMemo(() => active ? `${active.key}|${active.subject}|${active.body}` : "", [active]);
  useEffect(() => {
    if (!active) return;
    setPreviewLoading(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.post(`/super/email-templates/${active.key}/preview`, {
          subject: active.subject, body: active.body,
        });
        setPreview({ subject: data.subject, body: data.body });
      } catch {
        setPreview({ subject: "(Vorschau-Fehler)", body: "" });
      } finally {
        setPreviewLoading(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [draftKey, active]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/super/email-templates/${active.key}`, {
        subject: active.subject,
        body: active.body,
        enabled: active.enabled !== false,
      });
      load();
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!active) return;
    setTestBusy(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/super/email-templates/${active.key}/test-send`, {
        subject: active.subject, body: active.body, to: testTo || null,
      });
      setTestResult({ ok: data.ok, status: data.status, to: data.to, error: data.error });
    } catch (e) {
      setTestResult({ ok: false, status: "failed", error: e?.response?.data?.detail || "Fehler" });
    } finally {
      setTestBusy(false);
    }
  };

  if (!active) return <div className="p-8">Lade...</div>;
  const vars = VARIABLES[active.key] || [];

  return (
    <AdminLayout>
      <div className="p-8 lg:p-12 mv-fade-in max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="mv-label mb-2">Plattform-Texte</p>
          <h1 className="font-display text-4xl">E-Mail-Vorlagen (Plattform)</h1>
          <p className="text-sm mt-2" style={{ color: "var(--mv-text-muted)" }}>
            Diese Vorlagen gelten plattform-weit (StudioMV → Salon-Inhaber bzw. SuperAdmin) und werden
            über die Plattform-SMTP versendet.
          </p>
        </div>
        <button data-testid="save-super-template-btn" onClick={save} disabled={busy} className="mv-btn"><Save className="w-4 h-4" /> {busy ? "..." : "Speichern"}</button>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr_1fr] gap-6">
        <div>
          <p className="mv-label mb-3">Vorlagen</p>
          <div className="space-y-1">
            {list.map((t) => (
              <button key={t.key} data-testid={`super-tpl-${t.key}`} onClick={() => setActive(t)} className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${active.key === t.key ? "bg-[var(--mv-primary)] text-white" : "hover:bg-[var(--mv-surface)]"}`}>
                <span className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> {TEMPLATE_LABELS[t.key] || t.key}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="mv-card p-6">
            <label className="flex items-center justify-between mb-4 cursor-pointer">
              <div>
                <p className="text-sm font-medium" style={{ color: "var(--mv-text)" }}>Vorlage aktiv</p>
                <p className="text-xs mt-0.5" style={{ color: "var(--mv-text-muted)" }}>
                  Wenn deaktiviert, wird diese E-Mail nicht mehr automatisch versendet (manuelle Test-Sends bleiben möglich).
                </p>
              </div>
              <input
                type="checkbox"
                data-testid="super-template-enabled"
                checked={active.enabled !== false}
                onChange={(e) => setActive({ ...active, enabled: e.target.checked })}
                className="w-5 h-5 cursor-pointer"
              />
            </label>
            <p className="mv-label mb-2">Betreff</p>
            <input data-testid="super-template-subject" className="mv-input" value={active.subject} onChange={(e) => setActive({ ...active, subject: e.target.value })} />
            <p className="mv-label mb-2 mt-5">Inhalt</p>
            <textarea data-testid="super-template-body" className="mv-input min-h-[320px] font-mono text-sm leading-relaxed" value={active.body} onChange={(e) => setActive({ ...active, body: e.target.value })} />
            <p className="mv-label mt-5 mb-2">Verfügbare Variablen</p>
            <div className="flex flex-wrap gap-1.5">
              {vars.map((v) => (
                <button key={v} type="button" onClick={() => setActive({ ...active, body: active.body + `{{${v}}}` })} className="px-2.5 py-1 rounded-md text-xs border hover:bg-[var(--mv-surface)] font-mono" style={{ borderColor: "var(--mv-border)" }}>
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-4" data-testid="super-preview-panel">
          <div className="mv-card p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="mv-label flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" strokeWidth={1.5} /> Vorschau</p>
              {previewLoading && <span className="text-xs" style={{ color: "var(--mv-text-muted)" }}>rendert…</span>}
            </div>
            <p className="text-xs mb-3" style={{ color: "var(--mv-text-muted)" }}>Platzhalter werden mit Beispieldaten ersetzt.</p>
            <div className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--mv-border)" }}>
              <div className="px-4 py-2.5 text-xs" style={{ background: "var(--mv-surface)", color: "var(--mv-text-muted)" }}>
                <b style={{ color: "var(--mv-text)" }}>Betreff:</b>{" "}
                <span data-testid="super-preview-subject">{preview.subject || "(leer)"}</span>
              </div>
              <pre data-testid="super-preview-body" className="p-4 text-sm leading-relaxed whitespace-pre-wrap font-sans" style={{ background: "#fff", color: "var(--mv-text)", margin: 0, fontFamily: "inherit" }}>
                {preview.body || "(leer)"}
              </pre>
            </div>
          </div>

          <div className="mv-card p-6">
            <p className="mv-label mb-2 flex items-center gap-1.5"><Send className="w-3.5 h-3.5" strokeWidth={1.5} /> Test-E-Mail senden</p>
            <p className="text-xs mb-3" style={{ color: "var(--mv-text-muted)" }}>Sendet diese Vorlage mit Beispieldaten an die angegebene Adresse.</p>
            <div className="flex gap-2">
              <input data-testid="super-test-to-input" type="email" placeholder="empfaenger@example.com" className="mv-input flex-1 text-sm" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
              <button data-testid="super-test-send-btn" onClick={sendTest} disabled={testBusy || !testTo} className="mv-btn">
                <Send className="w-3.5 h-3.5" /> {testBusy ? "…" : "Senden"}
              </button>
            </div>
            {testResult && (
              <div data-testid="super-test-result" className="mt-3 text-xs px-3 py-2 rounded" style={{ background: testResult.ok ? "rgba(34,197,94,0.1)" : "rgba(220,38,38,0.1)", color: testResult.ok ? "#15803d" : "#b91c1c" }}>
                {testResult.ok
                  ? `✓ Erfolgreich an ${testResult.to} gesendet.`
                  : testResult.status === "queued"
                    ? `In Warteschlange — SMTP ist evtl. nicht konfiguriert (siehe SMTP-Einstellungen). ${testResult.error || ""}`
                    : `Fehler: ${testResult.error || "Unbekannt"}`}
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </AdminLayout>
  );
}
